import type { CSSProperties, ReactNode } from "react";

import type { Addendum } from "@/core/types";
import dayjs from "@/lib/helpers/dayjs";
import { Divider2 } from "@/ui2/atoms/Divider";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { SignatureList } from "@/features/notes/(v2)/components/(signature)/SignatureList";
import { testLocator } from "@/lib/helpers/test-locator";
import { TextField2 } from "@/ui2/molecules/TextField";

import { AddendumStateBadge } from "../AddendumStateBadge";

type GeneralAddendumDisplayProps = {
  addendum: Addendum;
  children?: ReactNode;
  style?: CSSProperties;
  textTestId?: string;
};

export function GeneralAddendumDisplay({
  addendum,
  children,
  style,
  textTestId,
}: GeneralAddendumDisplayProps) {
  const {
    addendum: text,
    addendum_state,
    cosigned_by,
    credentials,
    is_cosign_required,
    signature,
    timestamp,
  } = addendum;

  const signatureDate = timestamp ? dayjs.unix(timestamp).format("MM/DD/YYYY") : undefined;

  const isComplete = addendum_state === "complete";

  return (
    <div className="tw:flex tw:flex-col tw:gap-6" style={style}>
      {children}

      {text && (
        <TextField2
          hasStrongLabel
          isReadOnly
          label="Details"
          mode="textarea"
          textAreaProps={{
            value: text,
            ...getAutomationDataAttr(textTestId),
            ...testLocator("noteAddendumText"),
          }}
        />
      )}

      {(signature || cosigned_by || signatureDate) && (
        <>
          <Divider2 />

          <SignatureList
            cosignatureBadge={
              !isComplete ? <AddendumStateBadge state={addendum_state} /> : undefined
            }
            cosignedBy={isComplete ? cosigned_by : undefined}
            credentials={credentials}
            isCosignRequired={is_cosign_required}
            isNoteComplete={isComplete}
            signature={signature}
            signatureDate={signatureDate}
            testId="note-addendum-footer"
          />
        </>
      )}
    </div>
  );
}
